// AdminTimetableManagement.js - UPDATED WITH TABS FOR TEACHER AND STUDENT TIMETABLES
// Supports: Teacher Class Schedules + Student Timetables (Master, Periods, Entries)
// Similar tab structure to AttendanceManagement

import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminTimetableManagement = ({ onNavigate, userInfo = {} }) => {
  // =============================================
  // STATE MANAGEMENT
  // =============================================

  // UI State
  const [isMobile, setIsMobile] = useState(false);
  const [timetableType, setTimetableType] = useState('teacher'); // 'teacher' or 'student'

  // =============================================
  // TEACHER TIMETABLE STATE (svu_timetable_class_schedules)
  // =============================================
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Teacher Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [limit] = useState(10);

  // Teacher Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Teacher Form state
  const [formData, setFormData] = useState({
    day: '',
    time_slot: '',
    subject: '',
    teacher_username: '',
    location: '',
    batch_name: '',
    student_count: ''
  });

  // =============================================
  // STUDENT TIMETABLE STATE (svu_timetable_master + periods + entries)
  // =============================================
  const [studentTimetables, setStudentTimetables] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState(null);

  // Student Pagination
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [studentTotalPages, setStudentTotalPages] = useState(1);
  const [studentTotalCount, setStudentTotalCount] = useState(0);

  // Student Filters
  const [studentDeptFilter, setStudentDeptFilter] = useState('');
  const [studentYearFilter, setStudentYearFilter] = useState('');
  const [studentBatchFilter, setStudentBatchFilter] = useState('');
  const [studentSemesterFilter, setStudentSemesterFilter] = useState('');
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [studentActiveFilter, setStudentActiveFilter] = useState('');

  // Student Modal states
  const [showStudentAddModal, setShowStudentAddModal] = useState(false);
  const [showStudentEditModal, setShowStudentEditModal] = useState(false);
  const [showStudentDeleteModal, setShowStudentDeleteModal] = useState(false);
  const [selectedStudentTimetable, setSelectedStudentTimetable] = useState(null);

  // Student Periods Modal
  const [showPeriodsModal, setShowPeriodsModal] = useState(false);
  const [selectedTimetablePeriods, setSelectedTimetablePeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);

  // Student Entries Modal
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [selectedTimetableEntries, setSelectedTimetableEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Add/Edit Period Modal
  const [showPeriodFormModal, setShowPeriodFormModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodFormData, setPeriodFormData] = useState({
    period_number: '',
    period_name: '',
    start_time: '',
    end_time: '',
    is_break: false,
    break_name: ''
  });

  // Add/Edit Entry Modal
  const [showEntryFormModal, setShowEntryFormModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryFormData, setEntryFormData] = useState({
    period_id: '',
    day_of_week: '',
    subject_name: '',
    subject_code: '',
    teacher_name: '',
    teacher_id: '',
    room_number: '',
    class_type: 'lecture',
    notes: ''
  });

  // Student Timetable Form
  const [studentFormData, setStudentFormData] = useState({
    timetable_name: '',
    department: '',
    batch_name: '',
    year: '',
    semester: '',
    academic_year: '',
    effective_from: '',
    effective_to: '',
    is_active: true
  });

  // Departments List (dynamic or static)
  const [departments, setDepartments] = useState([]);

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Days of the week
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const daysOfWeekLower = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const classTypes = ['lecture', 'lab', 'tutorial', 'practical', 'seminar'];

  // =============================================
  // UTILITY FUNCTIONS
  // =============================================



  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  // =============================================
  // MOBILE CHECK
  // =============================================

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // =============================================
  // API HELPER FUNCTION
  // =============================================

  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // =============================================
  // FETCH DEPARTMENTS
  // =============================================

  const fetchDepartments = async () => {
    try {
      const result = await apiCall('/departments');
      if (result.success && result.data) {
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      // Set default departments if API fails
      setDepartments([
        { id: 1, name: 'BCA', code: 'BCA' },
        { id: 2, name: 'MCA', code: 'MCA' },
        { id: 3, name: 'BBA', code: 'BBA' },
        { id: 4, name: 'MBA', code: 'MBA' },
        { id: 5, name: 'B.Tech', code: 'BTECH' },
        { id: 6, name: 'M.Tech', code: 'MTECH' }
      ]);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  // =============================================
  // TEACHER TIMETABLE FUNCTIONS
  // =============================================

  const fetchSchedules = async (page = 1, search = '', day = '', batch = '', teacher = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() }),
        ...(day && day.trim() && { day: day.trim() }),
        ...(batch && batch.trim() && { batch_name: batch.trim() }),
        ...(teacher && teacher.trim() && { teacher_username: teacher.trim() })
      });

      const apiUrl = `${API_LINK}/api/admin/timetable?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        const schedulesData = result.data?.schedules || [];
        const paginationData = result.data?.pagination || {};

        setSchedules(schedulesData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || schedulesData.length);
      } else {
        setError(result.message || 'Failed to fetch timetable from server');
      }
    } catch (error) {
      setError(`Failed to load timetable: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // =============================================
  // STUDENT TIMETABLE FUNCTIONS
  // =============================================

  const fetchStudentTimetables = async () => {
    try {
      setStudentLoading(true);
      setStudentError(null);

      const params = new URLSearchParams();
      if (studentDeptFilter) params.append('department', studentDeptFilter);
      if (studentYearFilter) params.append('year', studentYearFilter);
      if (studentBatchFilter) params.append('batch_name', studentBatchFilter);
      if (studentActiveFilter !== '') params.append('is_active', studentActiveFilter);

      const result = await apiCall(`/timetables?${params.toString()}`);

      if (result.success) {
        let timetables = result.data || [];

        // Client-side search filter
        if (studentSearchTerm.trim()) {
          const searchLower = studentSearchTerm.toLowerCase();
          timetables = timetables.filter(t =>
            t.timetable_name?.toLowerCase().includes(searchLower) ||
            t.department?.toLowerCase().includes(searchLower) ||
            t.batch_name?.toLowerCase().includes(searchLower)
          );
        }

        setStudentTimetables(timetables);
        setStudentTotalCount(timetables.length);
        setStudentTotalPages(Math.ceil(timetables.length / 10));
      } else {
        setStudentError(result.message || 'Failed to fetch student timetables');
      }
    } catch (error) {
      setStudentError(`Failed to load student timetables: ${error.message}`);
    } finally {
      setStudentLoading(false);
    }
  };

  const fetchTimetablePeriods = async (timetableId) => {
    try {
      setPeriodsLoading(true);
      const result = await apiCall(`/timetables/${timetableId}/periods`);

      if (result.success) {
        setSelectedTimetablePeriods(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
      alert('Failed to load periods');
    } finally {
      setPeriodsLoading(false);
    }
  };

  const fetchTimetableEntries = async (timetableId) => {
    try {
      setEntriesLoading(true);
      const result = await apiCall(`/timetables/${timetableId}`);

      if (result.success && result.data) {
        setSelectedTimetablePeriods(result.data.periods || []);
        setSelectedTimetableEntries(result.data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      alert('Failed to load timetable entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  // =============================================
  // INITIAL DATA FETCH
  // =============================================

  useEffect(() => {
    if (timetableType === 'teacher') {
      fetchSchedules(1, '', '', '', '');
    } else {
      fetchStudentTimetables();
    }
  }, [timetableType]);

  // =============================================
  // HANDLE TIMETABLE TYPE CHANGE
  // =============================================

  const handleTimetableTypeChange = (type) => {
    setTimetableType(type);
    // Reset pagination and filters
    if (type === 'teacher') {
      setCurrentPage(1);
      setSearchTerm('');
      setDayFilter('');
      setBatchFilter('');
      setTeacherFilter('');
    } else {
      setStudentCurrentPage(1);
      setStudentSearchTerm('');
      setStudentDeptFilter('');
      setStudentYearFilter('');
      setStudentBatchFilter('');
      setStudentSemesterFilter('');
      setStudentActiveFilter('');
    }
  };

  // =============================================
  // TEACHER TIMETABLE HANDLERS
  // =============================================

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSchedules(1, searchTerm, dayFilter, batchFilter, teacherFilter);
  };

  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchSchedules(page, searchTerm, dayFilter, batchFilter, teacherFilter);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.day.trim()) errors.day = 'Day is required';
    if (!formData.time_slot.trim()) errors.time_slot = 'Time slot is required';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.teacher_username.trim()) errors.teacher_username = 'Teacher is required';
    if (!formData.location.trim()) errors.location = 'Location is required';
    if (!formData.batch_name.trim()) errors.batch_name = 'Batch name is required';

    if (formData.student_count && (isNaN(formData.student_count) || parseInt(formData.student_count) < 0)) {
      errors.student_count = 'Student count must be a valid number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedSchedule
        ? `${API_LINK}/api/admin/timetable/${selectedSchedule.id}`
        : `${API_LINK}/api/admin/timetable`;

      const method = selectedSchedule ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        student_count: formData.student_count ? parseInt(formData.student_count) : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
        fetchSchedules(currentPage || 1, searchTerm, dayFilter, batchFilter, teacherFilter);
        alert(selectedSchedule ? 'Schedule updated successfully!' : 'Schedule added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert('Failed to save schedule. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/timetable/${selectedSchedule.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedSchedule(null);
        fetchSchedules(currentPage || 1, searchTerm, dayFilter, batchFilter, teacherFilter);
        alert('Schedule deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete schedule. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      day: '',
      time_slot: '',
      subject: '',
      teacher_username: '',
      location: '',
      batch_name: '',
      student_count: ''
    });
    setFormErrors({});
    setSelectedSchedule(null);
  };

  const handleEditClick = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      day: schedule.day || '',
      time_slot: schedule.time_slot || '',
      subject: schedule.subject || '',
      teacher_username: schedule.teacher_username || '',
      location: schedule.location || '',
      batch_name: schedule.batch_name || '',
      student_count: schedule.student_count?.toString() || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (schedule) => {
    setSelectedSchedule(schedule);
    setShowDeleteModal(true);
  };

  // =============================================
  // STUDENT TIMETABLE HANDLERS
  // =============================================

  const validateStudentForm = () => {
    const errors = {};

    if (!studentFormData.timetable_name.trim()) errors.timetable_name = 'Timetable name is required';
    if (!studentFormData.department.trim()) errors.department = 'Department is required';
    if (!studentFormData.batch_name.trim()) errors.batch_name = 'Batch name is required';
    if (!studentFormData.year) errors.year = 'Year is required';
    if (!studentFormData.effective_from) errors.effective_from = 'Effective from date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStudentSubmit = async () => {
    if (!validateStudentForm()) return;

    try {
      setSubmitting(true);

      const url = selectedStudentTimetable
        ? `/timetables/${selectedStudentTimetable.id}`
        : '/timetables';

      const method = selectedStudentTimetable ? 'PUT' : 'POST';

      const result = await apiCall(url, {
        method,
        body: JSON.stringify(studentFormData)
      });

      if (result.success) {
        setShowStudentAddModal(false);
        setShowStudentEditModal(false);
        resetStudentForm();
        fetchStudentTimetables();
        alert(selectedStudentTimetable ? 'Student timetable updated successfully!' : 'Student timetable created successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert(`Failed to save student timetable: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStudentDelete = async () => {
    if (!selectedStudentTimetable) return;

    try {
      setSubmitting(true);

      const result = await apiCall(`/timetables/${selectedStudentTimetable.id}`, {
        method: 'DELETE'
      });

      if (result.success) {
        setShowStudentDeleteModal(false);
        setSelectedStudentTimetable(null);
        fetchStudentTimetables();
        alert('Student timetable deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert(`Failed to delete: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetStudentForm = () => {
    setStudentFormData({
      timetable_name: '',
      department: '',
      batch_name: '',
      year: '',
      semester: '',
      academic_year: '',
      effective_from: '',
      effective_to: '',
      is_active: true
    });
    setFormErrors({});
    setSelectedStudentTimetable(null);
  };

  const handleStudentEditClick = (timetable) => {
    setSelectedStudentTimetable(timetable);
    setStudentFormData({
      timetable_name: timetable.timetable_name || '',
      department: timetable.department || '',
      batch_name: timetable.batch_name || '',
      year: timetable.year || '',
      semester: timetable.semester || '',
      academic_year: timetable.academic_year || '',
      effective_from: timetable.effective_from ? timetable.effective_from.split('T')[0] : '',
      effective_to: timetable.effective_to ? timetable.effective_to.split('T')[0] : '',
      is_active: timetable.is_active === 1 || timetable.is_active === true
    });
    setShowStudentEditModal(true);
  };

  const handleStudentDeleteClick = (timetable) => {
    setSelectedStudentTimetable(timetable);
    setShowStudentDeleteModal(true);
  };

  const handleViewPeriods = (timetable) => {
    setSelectedStudentTimetable(timetable);
    fetchTimetablePeriods(timetable.id);
    setShowPeriodsModal(true);
  };

  const handleViewEntries = (timetable) => {
    setSelectedStudentTimetable(timetable);
    fetchTimetableEntries(timetable.id);
    setShowEntriesModal(true);
  };

  // =============================================
  // PERIOD HANDLERS
  // =============================================

  const handleAddPeriod = () => {
    setSelectedPeriod(null);
    setPeriodFormData({
      period_number: '',
      period_name: '',
      start_time: '',
      end_time: '',
      is_break: false,
      break_name: ''
    });
    setShowPeriodFormModal(true);
  };

  const handleEditPeriod = (period) => {
    setSelectedPeriod(period);
    setPeriodFormData({
      period_number: period.period_number?.toString() || '',
      period_name: period.period_name || '',
      start_time: period.start_time || '',
      end_time: period.end_time || '',
      is_break: period.is_break === 1 || period.is_break === true,
      break_name: period.break_name || ''
    });
    setShowPeriodFormModal(true);
  };

  const handlePeriodSubmit = async () => {
    if (!periodFormData.period_number || !periodFormData.period_name || !periodFormData.start_time || !periodFormData.end_time) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setSubmitting(true);

      const url = selectedPeriod
        ? `/periods/${selectedPeriod.id}`
        : `/timetables/${selectedStudentTimetable.id}/periods`;

      const method = selectedPeriod ? 'PUT' : 'POST';

      const result = await apiCall(url, {
        method,
        body: JSON.stringify({
          ...periodFormData,
          period_number: parseInt(periodFormData.period_number),
          is_break: periodFormData.is_break ? 1 : 0
        })
      });

      if (result.success) {
        setShowPeriodFormModal(false);
        fetchTimetablePeriods(selectedStudentTimetable.id);
        alert(selectedPeriod ? 'Period updated!' : 'Period added!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert(`Failed: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePeriod = async (period) => {
    if (!window.confirm(`Delete period "${period.period_name}"?`)) return;

    try {
      const result = await apiCall(`/periods/${period.id}`, { method: 'DELETE' });
      if (result.success) {
        fetchTimetablePeriods(selectedStudentTimetable.id);
        alert('Period deleted!');
      }
    } catch (error) {
      alert(`Failed: ${error.message}`);
    }
  };

  // =============================================
  // ENTRY HANDLERS
  // =============================================

  const handleAddEntry = () => {
    setSelectedEntry(null);
    setEntryFormData({
      period_id: '',
      day_of_week: '',
      subject_name: '',
      subject_code: '',
      teacher_name: '',
      teacher_id: '',
      room_number: '',
      class_type: 'lecture',
      notes: ''
    });
    setShowEntryFormModal(true);
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setEntryFormData({
      period_id: entry.period_id?.toString() || '',
      day_of_week: entry.day_of_week || '',
      subject_name: entry.subject_name || '',
      subject_code: entry.subject_code || '',
      teacher_name: entry.teacher_name || '',
      teacher_id: entry.teacher_id || '',
      room_number: entry.room_number || '',
      class_type: entry.class_type || 'lecture',
      notes: entry.notes || ''
    });
    setShowEntryFormModal(true);
  };

  const handleEntrySubmit = async () => {
    if (!entryFormData.period_id || !entryFormData.day_of_week) {
      alert('Please select period and day');
      return;
    }

    try {
      setSubmitting(true);

      const url = selectedEntry
        ? `/entries/${selectedEntry.id}`
        : `/timetables/${selectedStudentTimetable.id}/entries`;

      const method = selectedEntry ? 'PUT' : 'POST';

      const result = await apiCall(url, {
        method,
        body: JSON.stringify({
          ...entryFormData,
          period_id: parseInt(entryFormData.period_id)
        })
      });

      if (result.success) {
        setShowEntryFormModal(false);
        fetchTimetableEntries(selectedStudentTimetable.id);
        alert(selectedEntry ? 'Entry updated!' : 'Entry added!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert(`Failed: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Delete this entry for ${entry.subject_name || 'this period'}?`)) return;

    try {
      const result = await apiCall(`/entries/${entry.id}`, { method: 'DELETE' });
      if (result.success) {
        fetchTimetableEntries(selectedStudentTimetable.id);
        alert('Entry deleted!');
      }
    } catch (error) {
      alert(`Failed: ${error.message}`);
    }
  };

  // =============================================
  // CLEAR FILTERS
  // =============================================

  const handleClearFilters = () => {
    if (timetableType === 'teacher') {
      setSearchTerm('');
      setDayFilter('');
      setBatchFilter('');
      setTeacherFilter('');
      setCurrentPage(1);
      fetchSchedules(1, '', '', '', '');
    } else {
      setStudentSearchTerm('');
      setStudentDeptFilter('');
      setStudentYearFilter('');
      setStudentBatchFilter('');
      setStudentSemesterFilter('');
      setStudentActiveFilter('');
      setStudentCurrentPage(1);
      fetchStudentTimetables();
    }
  };



  // =============================================
  // RENDER COMPONENT
  // =============================================

  return (
    <Layout role="admin" onNavigate={onNavigate} userInfo={userInfo} activePage="admin-timetable">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Timetable Type Selector - TABS */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <span style={{ fontSize: '2rem' }}>📅</span>
          <h3 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#ffffff'
          }}>
            Timetable Management
          </h3>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '1rem'
        }}>
          {/* Teacher Timetable Button */}
          <button
            onClick={() => handleTimetableTypeChange('teacher')}
            style={{
              padding: '2rem',
              border: timetableType === 'teacher' ? '3px solid #ffffff' : '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              background: timetableType === 'teacher'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(255, 255, 255, 0.1)',
              color: timetableType === 'teacher' ? '#238548' : '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 600,
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              if (timetableType !== 'teacher') {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (timetableType !== 'teacher') {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '2rem' }}>👨‍🏫</span>
              <span style={{ fontSize: '1.3rem', fontWeight: 700 }}>Teacher Timetable</span>
            </div>
            <p style={{
              margin: 0,
              fontSize: '0.9rem',
              opacity: 0.8,
              lineHeight: 1.4
            }}>
              Manage teacher class schedules with day, time slots, and locations
            </p>
          </button>

          {/* Student Timetable Button */}
          <button
            onClick={() => handleTimetableTypeChange('student')}
            style={{
              padding: '2rem',
              border: timetableType === 'student' ? '3px solid #ffffff' : '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              background: timetableType === 'student'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(255, 255, 255, 0.1)',
              color: timetableType === 'student' ? '#238548' : '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 600,
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              if (timetableType !== 'student') {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (timetableType !== 'student') {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '2rem' }}>🎓</span>
              <span style={{ fontSize: '1.3rem', fontWeight: 700 }}>Student Timetable</span>
            </div>
            <p style={{
              margin: 0,
              fontSize: '0.9rem',
              opacity: 0.8,
              lineHeight: 1.4
            }}>
              Manage student timetables by department, batch, year with periods and entries
            </p>
          </button>
        </div>
      </div>

      {/* Page Header with Add Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? '1.5rem' : '2rem', color: '#333' }}>
          {timetableType === 'teacher' ? '👨‍🏫 Teacher Class Schedules' : '🎓 Student Timetables'}
        </h2>
        <button
          onClick={() => {
            if (timetableType === 'teacher') {
              resetForm();
              setShowAddModal(true);
            } else {
              resetStudentForm();
              setShowStudentAddModal(true);
            }
          }}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          <span>➕</span>
          {timetableType === 'teacher' ? 'Add Schedule' : 'Add Timetable'}
        </button>
      </div>

      {/* Filters Section */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
          🔍 {timetableType === 'teacher' ? 'Filter Teacher Schedules' : 'Filter Student Timetables'}
        </h3>

        {timetableType === 'teacher' ? (
          /* Teacher Filters */
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                🔎 Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search subject, teacher..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                📅 Day
              </label>
              <select
                value={dayFilter}
                onChange={(e) => setDayFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Days</option>
                {daysOfWeek.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                🎓 Batch
              </label>
              <input
                type="text"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                placeholder="Search by batch..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                👨‍🏫 Teacher
              </label>
              <input
                type="text"
                value={teacherFilter}
                onChange={(e) => setTeacherFilter(e.target.value)}
                placeholder="Search by teacher..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        ) : (
          /* Student Filters */
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                🔎 Search
              </label>
              <input
                type="text"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder="Search timetable name..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                🏢 Department
              </label>
              <input
                type="text"
                value={studentDeptFilter}
                onChange={(e) => setStudentDeptFilter(e.target.value)}
                placeholder="Department..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                📅 Year
              </label>
              <input
                type="text"
                value={studentYearFilter}
                onChange={(e) => setStudentYearFilter(e.target.value)}
                placeholder="Year..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                🎓 Batch
              </label>
              <input
                type="text"
                value={studentBatchFilter}
                onChange={(e) => setStudentBatchFilter(e.target.value)}
                placeholder="Batch name..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                ✅ Status
              </label>
              <select
                value={studentActiveFilter}
                onChange={(e) => setStudentActiveFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        )}

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (timetableType === 'teacher') {
                handleSearch();
              } else {
                fetchStudentTimetables();
              }
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔍 Apply Filters
          </button>

          <button
            onClick={handleClearFilters}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🗑️ Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
            {timetableType === 'teacher'
              ? `📋 Showing ${schedules.length} of ${totalCount} Schedules`
              : `📋 Showing ${studentTimetables.length} Timetables`
            }
          </h3>
        </div>

        {/* Loading / Error / Data */}
        {(timetableType === 'teacher' ? loading : studentLoading) ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#6b7280' }}>Loading timetable data...</p>
          </div>
        ) : (timetableType === 'teacher' ? error : studentError) ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <span style={{ fontSize: '3rem' }}>⚠️</span>
            <p style={{ color: '#dc2626', marginTop: '1rem' }}>
              {timetableType === 'teacher' ? error : studentError}
            </p>
            <button
              onClick={() => timetableType === 'teacher' ? fetchSchedules() : fetchStudentTimetables()}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#238548',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              🔄 Retry
            </button>
          </div>
        ) : (
          <>
            {/* Teacher Timetable Table */}
            {timetableType === 'teacher' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Day</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Time Slot</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Subject</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Teacher</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Location</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Batch</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Students</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                          <span style={{ fontSize: '3rem' }}>📭</span>
                          <p>No schedules found</p>
                        </td>
                      </tr>
                    ) : (
                      schedules.map((schedule, index) => (
                        <tr key={schedule.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.day}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.time_slot}</td>
                          <td style={{ padding: '1rem', color: '#374151', fontWeight: '500' }}>{schedule.subject}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.teacher_username}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.location}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.batch_name}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{schedule.student_count || '-'}</td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleEditClick(schedule)}
                                style={{
                                  padding: '0.5rem',
                                  background: '#3b82f6',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteClick(schedule)}
                                style={{
                                  padding: '0.5rem',
                                  background: '#dc2626',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Student Timetable Table */}
            {timetableType === 'student' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Department</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Batch</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Year</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Semester</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Effective From</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Periods</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentTimetables.length === 0 ? (
                      <tr>
                        <td colSpan="9" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                          <span style={{ fontSize: '3rem' }}>📭</span>
                          <p>No student timetables found</p>
                        </td>
                      </tr>
                    ) : (
                      studentTimetables.map((timetable, index) => (
                        <tr key={timetable.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '1rem', color: '#374151', fontWeight: '500' }}>{timetable.timetable_name}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{timetable.department}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{timetable.batch_name}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{timetable.year}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{timetable.semester || '-'}</td>
                          <td style={{ padding: '1rem', color: '#374151' }}>{formatDate(timetable.effective_from)}</td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background: timetable.is_active ? '#dcfce7' : '#fee2e2',
                              color: timetable.is_active ? '#166534' : '#dc2626'
                            }}>
                              {timetable.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background: '#e0f2fe',
                              color: '#0369a1'
                            }}>
                              {timetable.period_count || 0} periods
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleViewPeriods(timetable)}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  background: '#8b5cf6',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem'
                                }}
                                title="Manage Periods"
                              >
                                ⏰
                              </button>
                              <button
                                onClick={() => handleViewEntries(timetable)}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  background: '#f59e0b',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem'
                                }}
                                title="Manage Entries"
                              >
                                📚
                              </button>
                              <button
                                onClick={() => handleStudentEditClick(timetable)}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  background: '#3b82f6',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem'
                                }}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleStudentDeleteClick(timetable)}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  background: '#dc2626',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem'
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination for Teacher Timetable */}
            {timetableType === 'teacher' && totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? '#e5e7eb' : '#238548',
                    color: currentPage === 1 ? '#9ca3af' : '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ← Previous
                </button>
                <span style={{ padding: '0 1rem', color: '#374151' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === totalPages ? '#e5e7eb' : '#238548',
                    color: currentPage === totalPages ? '#9ca3af' : '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>


      {/* =============================================
          MODALS
          ============================================= */}

      {/* Teacher Schedule Add/Edit Modal */}
      {
        (showAddModal || showEditModal) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#333' }}>
                  {selectedSchedule ? '✏️ Edit Schedule' : '➕ Add New Schedule'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Day */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📅 Day *
                    </label>
                    <select
                      value={formData.day}
                      onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.day ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select Day</option>
                      {daysOfWeek.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                    {formErrors.day && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.day}</span>}
                  </div>

                  {/* Time Slot */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      ⏰ Time Slot *
                    </label>
                    <input
                      type="text"
                      value={formData.time_slot}
                      onChange={(e) => setFormData({ ...formData, time_slot: e.target.value })}
                      placeholder="e.g., 09:00 AM - 10:00 AM"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.time_slot ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.time_slot && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.time_slot}</span>}
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📚 Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Subject name"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.subject ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.subject && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.subject}</span>}
                  </div>

                  {/* Teacher */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      👨‍🏫 Teacher Username *
                    </label>
                    <input
                      type="text"
                      value={formData.teacher_username}
                      onChange={(e) => setFormData({ ...formData, teacher_username: e.target.value })}
                      placeholder="Teacher username"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.teacher_username ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.teacher_username && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.teacher_username}</span>}
                  </div>

                  {/* Location */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📍 Location *
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Room/Hall number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.location ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.location && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.location}</span>}
                  </div>

                  {/* Batch Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      🎓 Batch Name *
                    </label>
                    <input
                      type="text"
                      value={formData.batch_name}
                      onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                      placeholder="e.g., BCA-2024-A"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.batch_name ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.batch_name && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.batch_name}</span>}
                  </div>

                  {/* Student Count */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      👥 Student Count
                    </label>
                    <input
                      type="number"
                      value={formData.student_count}
                      onChange={(e) => setFormData({ ...formData, student_count: e.target.value })}
                      placeholder="Number of students"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.student_count ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.student_count && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.student_count}</span>}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Saving...' : (selectedSchedule ? '💾 Update Schedule' : '➕ Add Schedule')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Teacher Schedule Delete Modal */}
      {
        showDeleteModal && selectedSchedule && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗑️</div>
              <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626' }}>Delete Schedule</h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Are you sure you want to delete the schedule for <strong>{selectedSchedule.subject}</strong> on <strong>{selectedSchedule.day}</strong>?
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedSchedule(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : '#dc2626',
                    color: '#ffffff',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Deleting...' : '🗑️ Delete'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Student Timetable Add/Edit Modal */}
      {
        (showStudentAddModal || showStudentEditModal) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#333' }}>
                  {selectedStudentTimetable ? '✏️ Edit Student Timetable' : '➕ Create Student Timetable'}
                </h3>
                <button
                  onClick={() => {
                    setShowStudentAddModal(false);
                    setShowStudentEditModal(false);
                    resetStudentForm();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                  {/* Timetable Name */}
                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📋 Timetable Name *
                    </label>
                    <input
                      type="text"
                      value={studentFormData.timetable_name}
                      onChange={(e) => setStudentFormData({ ...studentFormData, timetable_name: e.target.value })}
                      placeholder="e.g., BCA 1st Year Sem 1 - 2024"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.timetable_name ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.timetable_name && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.timetable_name}</span>}
                  </div>

                  {/* Department */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      🏢 Department *
                    </label>
                    <input
                      type="text"
                      value={studentFormData.department}
                      onChange={(e) => setStudentFormData({ ...studentFormData, department: e.target.value })}
                      placeholder="e.g., BCA"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.department ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.department && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.department}</span>}
                  </div>

                  {/* Batch Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      🎓 Batch Name *
                    </label>
                    <input
                      type="text"
                      value={studentFormData.batch_name}
                      onChange={(e) => setStudentFormData({ ...studentFormData, batch_name: e.target.value })}
                      placeholder="e.g., BCA-2024-A"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.batch_name ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.batch_name && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.batch_name}</span>}
                  </div>

                  {/* Year */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📅 Year *
                    </label>
                    <input
                      type="number"
                      value={studentFormData.year}
                      onChange={(e) => setStudentFormData({ ...studentFormData, year: e.target.value })}
                      placeholder="e.g., 2024"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.year ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.year && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.year}</span>}
                  </div>

                  {/* Semester */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📖 Semester
                    </label>
                    <input
                      type="text"
                      value={studentFormData.semester}
                      onChange={(e) => setStudentFormData({ ...studentFormData, semester: e.target.value })}
                      placeholder="e.g., 1st, 2nd, Odd, Even"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Academic Year */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      🎓 Academic Year
                    </label>
                    <input
                      type="text"
                      value={studentFormData.academic_year}
                      onChange={(e) => setStudentFormData({ ...studentFormData, academic_year: e.target.value })}
                      placeholder="e.g., 2024-2025"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Effective From */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📅 Effective From *
                    </label>
                    <input
                      type="date"
                      value={studentFormData.effective_from}
                      onChange={(e) => setStudentFormData({ ...studentFormData, effective_from: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.effective_from ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {formErrors.effective_from && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{formErrors.effective_from}</span>}
                  </div>

                  {/* Effective To */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                      📅 Effective To
                    </label>
                    <input
                      type="date"
                      value={studentFormData.effective_to}
                      onChange={(e) => setStudentFormData({ ...studentFormData, effective_to: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Is Active */}
                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={studentFormData.is_active}
                        onChange={(e) => setStudentFormData({ ...studentFormData, is_active: e.target.checked })}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ fontWeight: '600', color: '#374151' }}>✅ Active Timetable</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowStudentAddModal(false);
                    setShowStudentEditModal(false);
                    resetStudentForm();
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStudentSubmit}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Saving...' : (selectedStudentTimetable ? '💾 Update Timetable' : '➕ Create Timetable')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Student Timetable Delete Modal */}
      {
        showStudentDeleteModal && selectedStudentTimetable && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗑️</div>
              <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626' }}>Delete Student Timetable</h3>
              <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
                Are you sure you want to delete <strong>{selectedStudentTimetable.timetable_name}</strong>?
              </p>
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                This will also delete all periods and entries for this timetable.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowStudentDeleteModal(false);
                    setSelectedStudentTimetable(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStudentDelete}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : '#dc2626',
                    color: '#ffffff',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Deleting...' : '🗑️ Delete'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Periods Management Modal */}
      {
        showPeriodsModal && selectedStudentTimetable && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#333' }}>
                    ⏰ Manage Periods
                  </h3>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6b7280' }}>
                    {selectedStudentTimetable.timetable_name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPeriodsModal(false);
                    setSelectedStudentTimetable(null);
                    setSelectedTimetablePeriods([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem' }}>
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleAddPeriod}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  >
                    ➕ Add Period
                  </button>
                </div>

                {periodsLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '4px solid #e5e7eb',
                      borderTop: '4px solid #238548',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto'
                    }}></div>
                  </div>
                ) : selectedTimetablePeriods.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    <span style={{ fontSize: '2rem' }}>📭</span>
                    <p>No periods defined yet. Add periods to build your timetable structure.</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>#</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Period Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Start Time</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>End Time</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Break?</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTimetablePeriods.map((period) => (
                        <tr key={period.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem' }}>{period.period_number}</td>
                          <td style={{ padding: '0.75rem', fontWeight: '500' }}>{period.period_name}</td>
                          <td style={{ padding: '0.75rem' }}>{formatTime(period.start_time)}</td>
                          <td style={{ padding: '0.75rem' }}>{formatTime(period.end_time)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            {period.is_break ? (
                              <span style={{ color: '#f59e0b' }}>☕ {period.break_name || 'Break'}</span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <button
                              onClick={() => handleEditPeriod(period)}
                              style={{
                                padding: '0.3rem 0.5rem',
                                background: '#3b82f6',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '0.5rem',
                                fontSize: '0.8rem'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeletePeriod(period)}
                              style={{
                                padding: '0.3rem 0.5rem',
                                background: '#dc2626',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Period Add/Edit Form Modal */}
      {
        showPeriodFormModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '500px',
              width: '100%'
            }}>
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                  {selectedPeriod ? '✏️ Edit Period' : '➕ Add Period'}
                </h3>
              </div>

              <div style={{ padding: '1.5rem 2rem' }}>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Period # *
                      </label>
                      <input
                        type="number"
                        value={periodFormData.period_number}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, period_number: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Period Name *
                      </label>
                      <input
                        type="text"
                        value={periodFormData.period_name}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, period_name: e.target.value })}
                        placeholder="e.g., Period 1"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={periodFormData.start_time}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, start_time: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        End Time *
                      </label>
                      <input
                        type="time"
                        value={periodFormData.end_time}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, end_time: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={periodFormData.is_break}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, is_break: e.target.checked })}
                      />
                      <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>☕ This is a break period</span>
                    </label>
                  </div>

                  {periodFormData.is_break && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                        Break Name
                      </label>
                      <input
                        type="text"
                        value={periodFormData.break_name}
                        onChange={(e) => setPeriodFormData({ ...periodFormData, break_name: e.target.value })}
                        placeholder="e.g., Lunch Break, Tea Break"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                padding: '1rem 2rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem'
              }}>
                <button
                  onClick={() => setShowPeriodFormModal(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePeriodSubmit}
                  disabled={submitting}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : '#238548',
                    color: '#ffffff',
                    borderRadius: '6px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Saving...' : 'Save Period'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Entries Management Modal */}
      {
        showEntriesModal && selectedStudentTimetable && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '95vw',
              width: '1200px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#333' }}>
                    📚 Manage Timetable Entries
                  </h3>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6b7280' }}>
                    {selectedStudentTimetable.timetable_name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEntriesModal(false);
                    setSelectedStudentTimetable(null);
                    setSelectedTimetableEntries([]);
                    setSelectedTimetablePeriods([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem' }}>
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleAddEntry}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  >
                    ➕ Add Entry
                  </button>
                </div>

                {entriesLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '4px solid #e5e7eb',
                      borderTop: '4px solid #238548',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto'
                    }}></div>
                  </div>
                ) : selectedTimetableEntries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    <span style={{ fontSize: '2rem' }}>📭</span>
                    <p>No entries yet. Add entries to fill in your timetable schedule.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Day</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Period</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Subject</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Code</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Teacher</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Room</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTimetableEntries.map((entry) => {
                          const period = selectedTimetablePeriods.find(p => p.id === entry.period_id);
                          return (
                            <tr key={entry.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.75rem', textTransform: 'capitalize' }}>{entry.day_of_week}</td>
                              <td style={{ padding: '0.75rem' }}>{period?.period_name || `Period ${entry.period_id}`}</td>
                              <td style={{ padding: '0.75rem', fontWeight: '500' }}>{entry.subject_name || '-'}</td>
                              <td style={{ padding: '0.75rem' }}>{entry.subject_code || '-'}</td>
                              <td style={{ padding: '0.75rem' }}>{entry.teacher_name || '-'}</td>
                              <td style={{ padding: '0.75rem' }}>{entry.room_number || '-'}</td>
                              <td style={{ padding: '0.75rem', textTransform: 'capitalize' }}>{entry.class_type || '-'}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleEditEntry(entry)}
                                  style={{
                                    padding: '0.3rem 0.5rem',
                                    background: '#3b82f6',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '0.5rem',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeleteEntry(entry)}
                                  style={{
                                    padding: '0.3rem 0.5rem',
                                    background: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Entry Add/Edit Form Modal */}
      {
        showEntryFormModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                  {selectedEntry ? '✏️ Edit Entry' : '➕ Add Entry'}
                </h3>
              </div>

              <div style={{ padding: '1.5rem 2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Period Selection */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      ⏰ Period *
                    </label>
                    <select
                      value={entryFormData.period_id}
                      onChange={(e) => setEntryFormData({ ...entryFormData, period_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="">Select Period</option>
                      {selectedTimetablePeriods.filter(p => !p.is_break).map(period => (
                        <option key={period.id} value={period.id}>
                          {period.period_name} ({formatTime(period.start_time)} - {formatTime(period.end_time)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Day Selection */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      📅 Day *
                    </label>
                    <select
                      value={entryFormData.day_of_week}
                      onChange={(e) => setEntryFormData({ ...entryFormData, day_of_week: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="">Select Day</option>
                      {daysOfWeekLower.map(day => (
                        <option key={day} value={day} style={{ textTransform: 'capitalize' }}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subject Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      📚 Subject Name
                    </label>
                    <input
                      type="text"
                      value={entryFormData.subject_name}
                      onChange={(e) => setEntryFormData({ ...entryFormData, subject_name: e.target.value })}
                      placeholder="e.g., Mathematics"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  {/* Subject Code */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      🏷️ Subject Code
                    </label>
                    <input
                      type="text"
                      value={entryFormData.subject_code}
                      onChange={(e) => setEntryFormData({ ...entryFormData, subject_code: e.target.value })}
                      placeholder="e.g., MATH101"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  {/* Teacher Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      👨‍🏫 Teacher Name
                    </label>
                    <input
                      type="text"
                      value={entryFormData.teacher_name}
                      onChange={(e) => setEntryFormData({ ...entryFormData, teacher_name: e.target.value })}
                      placeholder="Teacher name"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  {/* Room Number */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      🚪 Room Number
                    </label>
                    <input
                      type="text"
                      value={entryFormData.room_number}
                      onChange={(e) => setEntryFormData({ ...entryFormData, room_number: e.target.value })}
                      placeholder="e.g., Room 101"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  {/* Class Type */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      📝 Class Type
                    </label>
                    <select
                      value={entryFormData.class_type}
                      onChange={(e) => setEntryFormData({ ...entryFormData, class_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}
                    >
                      {classTypes.map(type => (
                        <option key={type} value={type} style={{ textTransform: 'capitalize' }}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                      📝 Notes
                    </label>
                    <textarea
                      value={entryFormData.notes}
                      onChange={(e) => setEntryFormData({ ...entryFormData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{
                padding: '1rem 2rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem'
              }}>
                <button
                  onClick={() => setShowEntryFormModal(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEntrySubmit}
                  disabled={submitting}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    background: submitting ? '#9ca3af' : '#238548',
                    color: '#ffffff',
                    borderRadius: '6px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {submitting ? 'Saving...' : 'Save Entry'}
                </button>
              </div>
            </div>
          </div>
        )
      }

    </Layout>
  );
};

export default AdminTimetableManagement;